import { useNavigation } from '@react-navigation/native';
import NetInfo from '@react-native-community/netinfo';
import moment from 'moment';
import React, { useEffect, useLayoutEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, TouchableOpacity, View, ActivityIndicator } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getTimestamp } from '~Utils/Time';
import { getLastMessage } from '~Utils/chat';
import { logScreenView } from '~Utils/firebaseAnalytics';
import { ChevronIcon, ChatIcon, PlusIcon, WifiOffIcon } from '~assets/icons';
import Button from '~components/Button';
import { ModernChatList, ModernChatHeader } from '~components/Chat';
import { ThemedStatusBar } from '~components/ThemedStatusBar/ThemedStatusBar';
import { SCREENS } from '~constants';
import useTabBar from '~containers/Core/navigation/AppScreens/zustand';
import { useGetBusinessAccount } from '~hooks/business/useGetBusinessAccount';
import { useGetUserAccount } from '~hooks/user/useGetUser';
import FirebaseChatsService from '~services/FirebaseChats';
import OfflineChatStorage from '~services/OfflineChatStorage/OfflineChatStorage';
import { Platform } from 'react-native';

// Get Firestore - platform-specific at runtime
function getFirestore(): any {
  try {
    if (Platform.OS === 'web') {
      console.log('🔍 [GETFIRESTORE] Platform is WEB - importing @react-native-firebase/firestore');
      // On web, import and call the stub's firestore function
      const firestoreModule = require('@react-native-firebase/firestore');
      console.log('🔍 [GETFIRESTORE] Module imported, keys:', Object.keys(firestoreModule || {}).slice(0, 5));
      const firestoreStub = firestoreModule.default;
      console.log('🔍 [GETFIRESTORE] .default is:', typeof firestoreStub);
      
      if (typeof firestoreStub !== 'function') {
        console.error('❌ [GETFIRESTORE] .default is NOT a function! Got:', typeof firestoreStub);
        return null;
      }
      
      const result = firestoreStub();
      console.log('🔍 [GETFIRESTORE] Called firestore(), got:', {
        type: typeof result,
        keys: result ? Object.keys(result).slice(0, 5) : 'null'
      });
      (window as any).__FIRESTORE_DEBUG__ = result;
      (window as any).__FIRESTORE_TYPE__ = typeof result;
      return result;
    } else {
      console.log('🔍 [GETFIRESTORE] Platform is NOT WEB, using react-native-firebase');
      // On mobile, use react-native-firebase
      const firestore = require('@react-native-firebase/firestore').default;
      return firestore();
    }
  } catch (error) {
    console.error('❌ [GETFIRESTORE] Exception:', (error as any).message);
    return null;
  }
}
import {
  CHAT_TYPE_ENUM,
  ChatType,
  ChatTypeWithKey,
  ConciergeChatMessageType,
  ConciergeChatType,
  ConciergeChatTypeWithKey,
} from '~types/chat';
import { NavigationProps } from '~types/navigation/navigation.type';
import { useTheme } from '~contexts/ThemeContext';
import { spacing, borderRadius, shadows, typography } from '~constants/design';
import { haptics } from '~Utils/haptics';
import { FadeIn } from '~components/MicroInteractions';
import { useUserStore } from '~providers/userStore/zustand';

export default function Chat() {
  const { colors } = useTheme();

  const user = useUserStore(state => state.user);
  const uid = user?.uid;
  const [chats, setChats] = useState<ChatTypeWithKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [hasOfflineData, setHasOfflineData] = useState(false);

  // Debug logging
  console.log('🔍 ChatView Debug:', {
    uid,
    isAuthenticated: !!user,
    chatsCount: chats.length,
    isLoading,
    isOnline,
    hasOfflineData,
  });

  const [sortedByTypeChats, setSortedByTypeChats] = useState<(ConciergeChatTypeWithKey | ChatTypeWithKey)[]>([]);
  const [chatWithConcierge, setChatWithConcierge] = useState<ConciergeChatTypeWithKey>();
  const [broadcastMessages, setBroadcastMessages] = useState([]);
  const [oneToOneMessages, setOneToOneMessages] = useState<ConciergeChatMessageType[]>([]);
  const [contactChat, setContactChat] = useState<ChatTypeWithKey | undefined>();
  const navigation = useNavigation<NavigationProps>();
  const { setIsTabBarDisabled } = useTabBar();
  const { t } = useTranslation();

  const { data: userAccount } = useGetUserAccount(user?.uid);
  const { data: hostBusiness } = useGetBusinessAccount('CmCCvWvpRVhhFlHdgU9QE6hbun82');

  useLayoutEffect(() => {
    navigation.addListener('focus', () => {
      setIsTabBarDisabled(false);
    });
  }, []);

  // Monitor network connectivity
  useEffect(() => {
    const unsubscribe = NetInfo.addEventListener(state => {
      const isConnected = !!(state.isConnected && state.isInternetReachable);
      setIsOnline(isConnected);

      if (!isConnected) {
        // Load offline data when going offline
        loadOfflineChats();
      }
    });

    return unsubscribe;
  }, []);

  // Check for offline data on mount and load if available
  useEffect(() => {
    const initializeOfflineData = async () => {
      await checkOfflineData();
      // Always try to load offline chats first for immediate display
      await loadOfflineChats();

      // If no offline chats found and we're online, ensure loading continues
      // The Firestore subscription will handle setting isLoading to false
    };

    initializeOfflineData();
  }, []);

  const checkOfflineData = async () => {
    try {
      const hasData = await OfflineChatStorage.hasOfflineData();
      setHasOfflineData(hasData);
    } catch (error) {
      console.error('Error checking offline data:', error);
    }
  };

  const loadOfflineChats = async () => {
    try {
      const offlineChats = await OfflineChatStorage.getStoredChats();
      console.log('📱 Attempting to load offline chats:', offlineChats.length);
      console.log('💾 [CACHE] Raw stored chats:', offlineChats.map((c: any) => ({ 
        id: c.key, 
        type: 'type' in c ? c.type : 'concierge',
        lastMessage: 'lastMessage' in c ? (typeof c.lastMessage === 'string' ? c.lastMessage.substring(0, 20) : 'N/A') : 'N/A'
      })));

      if (offlineChats.length > 0) {
        console.log('📱 Loading offline chats:', offlineChats.length);

        // Separate regular chats from concierge chats
        const regularChats = offlineChats.filter(chat => 'history' in chat) as ChatTypeWithKey[];
        const conciergeChats = offlineChats.filter(chat => 'messages' in chat) as ConciergeChatTypeWithKey[];

        console.log('📱 Regular chats:', regularChats.length, 'Concierge chats:', conciergeChats.length);
        console.log('💾 [CACHE] Regular chat IDs:', regularChats.map(c => ({ id: c.key, type: c.type })));

        // Only update if we have regular chats or if current chats are empty
        if (regularChats.length > 0 || chats.length === 0) {
          setChats(regularChats);
          console.log('💾 [CACHE] ✅ Set chats state with', regularChats.length, 'offline chats');
        } else {
          console.log('💾 [CACHE] ⏭️  Skipped chats update - found', regularChats.length, 'chats but state already has', chats.length);
        }

        if (conciergeChats.length > 0) {
          setChatWithConcierge(conciergeChats[0]);
          console.log('💾 [CACHE] ✅ Set concierge chat');
        }

        setHasOfflineData(true);
        console.log('📊 [STATE] hasOfflineData set to TRUE');
        // Only set loading to false if we're offline, otherwise let Firestore handle it
        if (!isOnline) {
          setIsLoading(false);
          console.log('📊 [STATE] isLoading set to FALSE (offline mode)');
        }
      } else {
        console.log('📱 No offline chats found');
        console.log('💾 [CACHE] Empty storage - no cached data');
        setHasOfflineData(false);
        console.log('📊 [STATE] hasOfflineData set to FALSE');
        // If we're offline and have no cached data, stop loading
        if (!isOnline) {
          setIsLoading(false);
          console.log('📊 [STATE] isLoading set to FALSE (offline with no cache)');
        }
        // If we're online, let the Firestore subscription handle loading state
      }
    } catch (error) {
      console.error('Error loading offline chats:', error);
      console.error('💾 [CACHE] Error:', error instanceof Error ? error.message : 'Unknown error');
      setHasOfflineData(false);
    }
  };

  const storeChatsOffline = async (chatsToStore: (ChatTypeWithKey | ConciergeChatTypeWithKey)[]) => {
    try {
      await OfflineChatStorage.storeChats(chatsToStore);
    } catch (error) {
      console.error('Error storing chats offline:', error);
    }
  };

  useEffect(() => {
    if (uid && uid !== 'CmCCvWvpRVhhFlHdgU9QE6hbun82' && hostBusiness?.uid && userAccount?.uid) { 
      FirebaseChatsService.createContactUsChat({
        user_id1: user?.uid + '',
        user_id2: hostBusiness?.uid + '',
        user_name1: `${userAccount?.first_name} ${userAccount?.last_name || ''}`,
        user_name2: hostBusiness?.name || 'Pyxi',
      });
    }
  }, [uid, hostBusiness, userAccount]);

  useEffect(() => {
    if (uid && uid !== 'CmCCvWvpRVhhFlHdgU9QE6hbun82') {
      // Only set loading to true if we don't have offline data showing
      if (!hasOfflineData) {
        setIsLoading(true);
      }

      console.log('🔥 [FIRESTORE] ChatView - Setting up Firestore listener for uid:', uid);
      console.log('🔥 [FIRESTORE] Current hasOfflineData state:', hasOfflineData);
      console.log('🔥 [FIRESTORE] Current chats state count:', chats.length);
      console.log('🔥 [DEBUG] About to call getFirestore()...');
      const firestore = getFirestore();
      console.log('🔥 [DEBUG] getFirestore() returned:', typeof firestore, Object.keys(firestore || {}).slice(0, 5));
      const unsubscribe = firestore
        .collection('chats')
        .where('userIds', 'array-contains', uid)
        .onSnapshot(
          (querySnapshot: any) => {
            try {
              console.log('🔥 [FIRESTORE] ⏰ onSnapshot FIRED (#' + Math.random().toFixed(6) + ')');
              console.log('🔥 [FIRESTORE] ChatView - onSnapshot fired with docs:', querySnapshot.docs?.length || 0);
              console.log('📊 [STATE-CHECK] At callback time - hasOfflineData:', hasOfflineData, 'chats.length:', chats.length);
              const conversations: ChatTypeWithKey[] = [];
              querySnapshot.forEach((documentSnapshot: any) => {
                const data = documentSnapshot.data() as ChatType;
                console.log('🔥 [FIRESTORE] ChatView - Processing chat doc:', { id: documentSnapshot.id, type: data.type });
                conversations.push({
                  ...data,
                  key: documentSnapshot.id,
                });
              });

              // Sort chats by timestamp of the last message (optimized)
              const sortedConversations = conversations.sort((a, b) => {
                const timestampA = getTimestamp(a.history);
                const timestampB = getTimestamp(b.history);
                return timestampB - timestampA; // For descending order
              });

              // Find support chat more efficiently
              const supportChat = sortedConversations.find(chat => chat.type === 'contact-pyxi-select');
              if (supportChat) {
                console.log('Support chat found:', supportChat.key);
                setContactChat(supportChat);
              }

              console.log('🔥 [SNAPSHOT] About to call setChats with:', sortedConversations.length, 'items');
              console.log('📊 [DATA-FLOW] Current state before update:', {
                firestoreCount: sortedConversations.length,
                currentChatsInState: chats.length,
                isOnline: isOnline,
                uid: uid,
              });
              
              // KEY FIX: Only update state if Firestore has actual data
              // If Firestore returns empty snapshot, DO NOT call setChats
              // This preserves offline-loaded chats while waiting for real data
              if (sortedConversations.length > 0) {
                setChats(sortedConversations);
                console.log('🔥 [SNAPSHOT] ✅ setChats CALLED with', sortedConversations.length, 'items');
                console.log('📊 [DATA-FLOW] Setting state to firestore data:', {
                  count: sortedConversations.length,
                  chats: sortedConversations.map((c: any) => ({ 
                    id: c.key, 
                    type: c.type, 
                    message: c.lastMessage?.substring(0, 30) 
                  })),
                });
              } else {
                console.log('🔥 [SNAPSHOT] ⏭️  SKIPPED - empty snapshot (offline chats remain intact)');
                console.log('📊 [DATA-FLOW] NOT calling setChats, keeping offline data:', {
                  firestoreCount: sortedConversations.length,
                  offlineCount: chats.length,
                  reason: 'Empty Firestore snapshot detected',
                });
              }
              setIsLoading(false);

              // Store chats offline when online
              if (isOnline && sortedConversations.length > 0) {
                storeChatsOffline(sortedConversations);
                console.log('💾 [CACHE] Offline storage updated with', sortedConversations.length, 'chats');
              }

              console.log('🔥 Online chats loaded:', sortedConversations.length);
            } catch (error) {
              console.error('Error processing chat snapshot:', error);
              setIsLoading(false);
            }
          },
          (error: any) => {
            console.error('Chat subscription error:', error);
            setIsLoading(false);
          },
        );

      // Clean up subscription on unmount
      return () => unsubscribe();
    }
  }, [uid, isOnline]);

  useEffect(() => {
    logScreenView('Chat', 'ChatView');
  }, []);

  useEffect(() => {
    const firestore = getFirestore();
    console.log('🔥 [FIRESTORE] ChatView - Setting up concierge chat listeners');
    
    const oneOnOneUnsubscribe = firestore
      .collection('conciergeChat')
      .where('userId', '==', uid)
      .where('type', '==', 'one-on-one')
      .onSnapshot((querySnapshot: any) => {
        console.log('🔥 [FIRESTORE] ChatView - Concierge one-on-one snapshot:', querySnapshot.docs?.length || 0);
        querySnapshot.forEach((documentSnapshot: any) => {
          const data = documentSnapshot.data() as ConciergeChatType;
          console.log('🔥 [FIRESTORE] ChatView - Processing concierge chat:', { id: documentSnapshot.id });
          if (data) {
            const sortedMessages = [...broadcastMessages, ...data.messages].sort((a, b) => {
              return moment(a.timestamp).valueOf() - moment(b.timestamp).valueOf();
            });
            const conciergeChat = {
              ...data,
              key: documentSnapshot.id,
              messages: [...sortedMessages],
            };
            setChatWithConcierge(conciergeChat);
            setOneToOneMessages(data.messages);

            // Store concierge chat offline when online
            if (isOnline) {
              storeChatsOffline([conciergeChat]);
            }
          }
        });
      });

    const broadcastUnsubscribe = firestore
      .collection('conciergeChat')
      .where('type', '==', 'broadcast')
      .onSnapshot((querySnapshot: any) => {
        console.log('🔥 [FIRESTORE] ChatView - Concierge broadcast snapshot:', querySnapshot.docs?.length || 0);
        querySnapshot.forEach((documentSnapshot: any) => {
          const data = documentSnapshot.data();
          console.log('🔥 [FIRESTORE] ChatView - Broadcast messages:', data.messages?.length || 0);
          setBroadcastMessages(data.messages);
        });
      });

    return () => {
      console.log('🔥 [FIRESTORE] ChatView - Cleaning up concierge listeners');
      oneOnOneUnsubscribe();
      broadcastUnsubscribe();
    };
  }, []);

  useEffect(() => {
    const sortedMessages = [...oneToOneMessages, ...broadcastMessages].sort((a, b) => {
      return moment(a.timestamp).valueOf() - moment(b.timestamp).valueOf();
    });
    setChatWithConcierge(prev => {
      if (prev) {
        return { ...prev, messages: [...sortedMessages] };
      }
    });
  }, [oneToOneMessages, broadcastMessages]);

  useEffect(() => {
    // Show all chats (no filtering by type since we removed tabs)
    let allChats = [...chats];

    // Add concierge chat if it exists
    if (chatWithConcierge) {
      allChats = [chatWithConcierge, ...allChats];
    }

    // Sort by timestamp (most recent first)
    const sortedChats = allChats.sort((a, b) => {
      const timestampA = getTimestamp(a.history);
      const timestampB = getTimestamp(b.history);
      return timestampB - timestampA;
    });

    console.log('🔥 [SORTED_CHATS] Updating sortedByTypeChats:', sortedChats.length, 'items');
    console.log('📊 [SORTED_DATA] New sorted chats:', sortedChats.map((c: any) => ({
      id: c.key,
      type: 'type' in c ? c.type : 'concierge',
      message: 'lastMessage' in c ? (typeof c.lastMessage === 'string' ? c.lastMessage.substring(0, 25) : 'N/A') : 'N/A'
    })));
    setSortedByTypeChats(sortedChats);
  }, [chats, chatWithConcierge, uid]);

  // Log render data
  useEffect(() => {
    console.log('🖼️  [RENDER] sortedByTypeChats changed - about to render:');
    console.log('📊 [RENDER-DATA]', {
      totalChats: sortedByTypeChats.length,
      isLoading: isLoading,
      isRefreshing: isRefreshing,
      chats: sortedByTypeChats.map((c: any) => ({
        id: c.key,
        type: 'type' in c ? c.type : 'concierge',
        lastMsg: 'lastMessage' in c ? (typeof c.lastMessage === 'string' ? c.lastMessage.substring(0, 20) : 'N/A') : 'N/A'
      }))
    });
  }, [sortedByTypeChats]);

  const handleRefreshChats = useCallback(async () => {
    setIsRefreshing(true);
    haptics.light();

    try {
      // Force refresh by briefly clearing and reloading data
      // The Firestore listener will automatically update with fresh data
      await new Promise(resolve => setTimeout(resolve, 500)); // Small delay for UX
    } catch (error) {
      console.error('Error refreshing chats:', error);
      haptics.error();
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  const modernChatsList = useMemo(() => {
    if (isLoading) {
      return (
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: spacing.xl,
          }}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text
            style={{
              marginTop: spacing.md,
              fontSize: typography.fontSize.base,
              color: colors.textSecondary,
              fontWeight: typography.fontWeight.medium,
            }}>
            {t('chat.loading_conversations')}
          </Text>
        </View>
      );
    }

    return (
      <ModernChatList
        chats={sortedByTypeChats}
        onRefresh={handleRefreshChats}
        // isLoading={isRefreshing} // Remove this prop as it's not used in ModernChatList
        emptyStateTitle={t('chat.no_conversations')}
        emptyStateSubtitle={t('chat.start_conversation')}
      />
    );
  }, [sortedByTypeChats, handleRefreshChats, isLoading, isRefreshing, colors, t]);

  const handleChatWithPyxi = () => {    
    if (contactChat) {
      const interlocutorId = contactChat?.userIds?.find(userId => userId !== uid);
      const interlocutorMessage = contactChat?.history?.find(message => message.sender_id === interlocutorId);
      const interlocutorImage = interlocutorMessage?.sender_image;

      const image = contactChat?.eventImage || interlocutorImage;
      const userIndex = contactChat?.userIds?.findIndex(user => user.toLowerCase() !== (uid || '').toLowerCase()) || 0;
      const userName = contactChat?.users?.[userIndex];

      navigation.navigate(SCREENS.USER_CHAT, { chatId: contactChat?.key, image: image, userName: userName });
    }
  };

  return (
    <View style={{ flex: 1, backgroundColor: colors.background }}>
      <ThemedStatusBar />

      {/* Modern Header */}
      <ModernChatHeader userName={t('chat.conversations')} showCallButtons={false} showMoreButton={false} />

      {/* Offline indicator */}
      {!isOnline && (
        <View
          style={{
            backgroundColor: colors.warning,
            paddingHorizontal: spacing.md,
            paddingVertical: spacing.xs,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <WifiOffIcon width={16} height={16} color={colors.white} style={{ marginRight: spacing.xs }} />
          <Text
            style={{
              color: colors.white,
              fontSize: 12,
              fontWeight: '500',
            }}>
            {hasOfflineData
              ? t('chat.offline_viewing_cached') || 'Offline - Viewing cached conversations'
              : t('chat.offline_no_data') || 'Offline - No cached data available'}
          </Text>
        </View>
      )}

      {/* Support Chat Quick Access */}
      {contactChat && (
        <FadeIn delay={100} duration={300}>
          <View
            style={{
              marginHorizontal: spacing.md,
              marginTop: spacing.md,
              marginBottom: spacing.sm,
            }}>
            <TouchableOpacity
              style={{
                backgroundColor: colors.primary,
                borderRadius: borderRadius.xl,
                padding: spacing.md,
                flexDirection: 'row',
                alignItems: 'center',
                ...shadows.md,
              }}
              onPress={handleChatWithPyxi}
              activeOpacity={0.8}>
              <View
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: borderRadius.full,
                  backgroundColor: colors.white,
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: spacing.md,
                }}>
                <ChatIcon color={colors.primary} />
              </View>

              <View style={{ flex: 1 }}>
                <Text
                  style={{
                    fontSize: typography.fontSize.base,
                    fontWeight: typography.fontWeight.semibold,
                    color: colors.white,
                    marginBottom: 2,
                  }}>
                  {t('chat.support_chat')}
                </Text>
                <Text
                  style={{
                    fontSize: typography.fontSize.sm,
                    color: colors.white,
                    opacity: 0.9,
                  }}>
                  {t('chat.get_help_instantly')}
                </Text>
              </View>

              <ChevronIcon color={colors.white} style={{ transform: [{ rotate: '180deg' }] }} />
            </TouchableOpacity>
          </View>
        </FadeIn>
      )}

      {/* Modern Chat List */}
      {modernChatsList}
    </View>
  );
}
